/**
 * Circles-mode osu! hitsound layer. Imported beatmaps carry per-note
 * "addition" accents — whistle, finish, clap — that a mapper layered on
 * top of the base hit to reconstruct the song's percussion as you play
 * (see {@link import('@shared/circlesChart').CircleHitObject.hitSound}).
 * This module synthesises those accents and stacks them on the shared
 * {@link scheduleHitClick} baseline so a hitsounded chart feels like the
 * real osu! map, while a plain chart keeps the existing click untouched.
 *
 * We synthesise rather than ship samples (no asset pipeline, no skin
 * licensing, fits the all-procedural SFX architecture). The envelopes /
 * filter cutoffs below are tuned to the acoustic fingerprint of osu!'s
 * default skin samples (duration, spectral centroid, attack, noisiness)
 * measured per bank, so the synth lands in the same perceptual ballpark
 * as the originals without copying them.
 *
 * Banks: `normal` is bright + standard; `soft` is gentler, quieter and
 * longer; `drum` is darker + drier (taiko-ish). All layers route into
 * whatever `destination` the caller passes (the SFX bus) and are
 * pre-trimmed so the SFX volume slider scales the sum linearly.
 */

import {
  HIT_SOUND_CLAP,
  HIT_SOUND_FINISH,
  HIT_SOUND_WHISTLE,
  type HitSampleSet,
} from '@shared/circlesChart';

/**
 * Combined trim for the addition stack. Sits below the base click's
 * own level so accents read as a layer ON the hit, not a replacement —
 * the click still says "you hit it", the accent says "...on the snare".
 */
const CIRCLES_HS_TRIM = 0.5;

/**
 * Per-bank synthesis parameters, derived from the measured fingerprint
 * of each osu! default-skin sample:
 *   - whistle: bright tonal ping (three sine partials stacked above the
 *     `f1` fundamental + a breath of band-passed noise). `decay` s,
 *     `gain` 0..1, `noise` 0..1 relative breath level.
 *   - finish:  long bright crash/cymbal (band-limited noise wash + a
 *     brighter strike transient). `hpHz` high-pass + `lpHz` low-pass
 *     ceiling bound the band, `decay` s wash length, `gain`.
 *   - clap:    sharp noisy snare/clap (band-passed noise with a 3-burst
 *     flam attack). `bpHz` band centre, `decay` s tail, `gain`,
 *     `attackMs` flam spread.
 */
interface BankParams {
  whistle: { f1: number; decay: number; gain: number; noise: number };
  finish: { hpHz: number; lpHz: number; decay: number; gain: number };
  clap: { bpHz: number; decay: number; gain: number; attackMs: number };
}

// Tuned against the measured fingerprint of osu!'s default skin samples
// (see `.build/osu-skin-ref` analysis): whistle is a bright tonal ping
// (high centroid), finish a band-limited crash, clap a mid snare. The
// `whistle.f1` is the fundamental; partials are stacked above it in
// {@link scheduleWhistle} to lift the centroid into osu's bright range.
const BANKS: Record<HitSampleSet, BankParams> = {
  // Bright default bank. Whistle ≈ centroid 7.5 kHz / ~330 ms; finish ≈
  // 8 kHz / ~760 ms wash; clap ≈ 5.3 kHz / ~380 ms, instant attack.
  normal: {
    whistle: { f1: 5200, decay: 0.30, gain: 0.22, noise: 0.16 },
    finish: { hpHz: 3000, lpHz: 12000, decay: 0.65, gain: 0.20 },
    clap: { bpHz: 2300, decay: 0.32, gain: 0.30, attackMs: 0.5 },
  },
  // Gentler bank: quieter, longer, slightly softer attack. Matches the
  // soft skin's lower peak (~0.67) and longer whistle/finish tails. The
  // real soft whistle is actually the brightest of the three skins, so f1
  // sits high here despite the lower gain.
  soft: {
    whistle: { f1: 5400, decay: 0.6, gain: 0.15, noise: 0.12 },
    finish: { hpHz: 2800, lpHz: 12000, decay: 0.55, gain: 0.15 },
    clap: { bpHz: 2050, decay: 0.30, gain: 0.21, attackMs: 1.5 },
  },
  // Taiko-ish bank, unused by shipped charts (kept so a future
  // SampleSet:Drum import still makes sound). Approximate, not faithful:
  // the real drum finish + clap are *tonal* low hits (near-zero noise),
  // but we render them through the same noise path as the other banks —
  // darker + drier here, which lands in the right register if not the
  // exact timbre. The whistle stays bright (the real drum whistle is the
  // brightest of the three skins).
  drum: {
    whistle: { f1: 5800, decay: 0.35, gain: 0.20, noise: 0.30 },
    finish: { hpHz: 700, lpHz: 5000, decay: 0.42, gain: 0.26 },
    clap: { bpHz: 1500, decay: 0.30, gain: 0.27, attackMs: 6 },
  },
};

/**
 * Stereo-pan helper. Falls back to the bare destination when
 * `createStereoPanner` is unavailable (older Safari). Mirrors the
 * panners in `sfx.ts` / `guitarSfx.ts` — kept local so this module is
 * self-contained.
 */
function panNode(ctx: AudioContext, destination: AudioNode, pan: number): AudioNode {
  if (typeof ctx.createStereoPanner !== 'function') {
    return destination;
  }
  const panner = ctx.createStereoPanner();
  panner.pan.value = Math.max(-1, Math.min(1, pan));
  panner.connect(destination);
  return panner;
}

/** Fill a mono buffer with flat white noise. */
function makeNoiseBuffer(ctx: AudioContext, durationSec: number): AudioBuffer {
  const samples = Math.max(1, Math.ceil(ctx.sampleRate * durationSec));
  const buffer = ctx.createBuffer(1, samples, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    data[i] = Math.random() * 2 - 1;
  }
  return buffer;
}

/**
 * Schedule the osu! hitsound additions encoded in `hitSound` (a bitmask
 * of {@link HIT_SOUND_WHISTLE} / {@link HIT_SOUND_FINISH} /
 * {@link HIT_SOUND_CLAP}) onto `destination` at time `t0`, using the
 * timbre of `sampleSet`. No-op when `hitSound` has no addition bits.
 * Returns the longest scheduled accent tail in seconds (0 when nothing
 * fired) so the caller can size its SFX-voice hold to the real sound
 * length — a finish wash is the long one, but a soft whistle also
 * outlasts a default hold.
 *
 * Layers compose into one panner so a single circle's accents share its
 * stereo position. Whistle + finish are tonal/bright; clap is the
 * backbeat. A note can carry several at once (e.g. clap+finish on a
 * chorus downbeat) — they're additive by design, exactly like osu!.
 */
export function scheduleCirclesHitsounds(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
  hitSound: number,
  sampleSet: HitSampleSet = 'normal',
  pan = 0,
): number {
  const additions = hitSound & (HIT_SOUND_WHISTLE | HIT_SOUND_FINISH | HIT_SOUND_CLAP);
  if (additions === 0) {
    return 0;
  }
  const bank = BANKS[sampleSet];
  const out = panNode(ctx, destination, pan);
  let maxTail = 0;
  if (additions & HIT_SOUND_WHISTLE) {
    maxTail = Math.max(maxTail, scheduleWhistle(ctx, out, t0, bank.whistle));
  }
  if (additions & HIT_SOUND_CLAP) {
    maxTail = Math.max(maxTail, scheduleClap(ctx, out, t0, bank.clap));
  }
  if (additions & HIT_SOUND_FINISH) {
    maxTail = Math.max(maxTail, scheduleFinish(ctx, out, t0, bank.finish));
  }
  return maxTail;
}

/**
 * Whistle: a bright tonal "ping". Three sine partials stacked above the
 * fundamental give the metallic-but-pitched character; a short burst of
 * band-passed noise centred on the fundamental adds the breathy "air"
 * that separates a whistle from a pure bell tone.
 */
function scheduleWhistle(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
  p: BankParams['whistle'],
): number {
  // Three partials stacked above the fundamental (1×, 1.5×, 2.2×) push
  // the spectral centroid up into osu!'s bright whistle range while the
  // tonal stack keeps it pitched rather than purely noisy.
  const partials: Array<{ hz: number; level: number }> = [
    { hz: p.f1, level: 1 },
    { hz: p.f1 * 1.5, level: 0.5 },
    { hz: p.f1 * 2.2, level: 0.3 },
  ];
  for (const { hz, level } of partials) {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = hz;
    const gain = ctx.createGain();
    const peak = p.gain * level * CIRCLES_HS_TRIM;
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(peak, t0 + 0.003);
    gain.gain.exponentialRampToValueAtTime(0.0008, t0 + p.decay);
    osc.connect(gain).connect(destination);
    osc.start(t0);
    osc.stop(t0 + p.decay + 0.02);
  }
  if (p.noise > 0) {
    const airDur = Math.min(0.05, p.decay);
    const noise = ctx.createBufferSource();
    noise.buffer = makeNoiseBuffer(ctx, airDur);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = p.f1;
    bp.Q.value = 4;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(p.noise * p.gain * CIRCLES_HS_TRIM, t0);
    gain.gain.exponentialRampToValueAtTime(0.0008, t0 + airDur);
    noise.connect(bp).connect(gain).connect(destination);
    noise.start(t0);
  }
  return p.decay + 0.02;
}

/**
 * Finish: a crash/cymbal splash. A looped high-passed noise buffer is
 * the bright wash; a separate, even-brighter short noise transient is
 * the initial "strike". The wash's long exponential decay carries the
 * cymbal tail — this is the accent for big downbeats, so it's the one
 * layer worth the extra length.
 */
function scheduleFinish(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
  p: BankParams['finish'],
): number {
  // Loop a short noise buffer rather than allocate a full decay-length
  // one — noise has no audible loop seam and finishes are sparse, so
  // this keeps per-hit allocation tiny even for a long wash.
  const wash = ctx.createBufferSource();
  wash.buffer = makeNoiseBuffer(ctx, 0.25);
  wash.loop = true;
  // High-pass for the crash brightness, then a low-pass ceiling so the
  // band is limited — pure high-passed white noise reads far brighter +
  // hissier (centroid ~13 kHz) than osu!'s finish (~8 kHz); the ceiling
  // pulls the centroid and zero-crossing rate back into range.
  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = p.hpHz;
  hp.Q.value = 0.5;
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = p.lpHz;
  lp.Q.value = 0.5;
  const washGain = ctx.createGain();
  washGain.gain.setValueAtTime(0, t0);
  washGain.gain.linearRampToValueAtTime(p.gain * CIRCLES_HS_TRIM, t0 + 0.004);
  washGain.gain.exponentialRampToValueAtTime(0.0006, t0 + p.decay);
  wash.connect(hp).connect(lp).connect(washGain).connect(destination);
  wash.start(t0);
  wash.stop(t0 + p.decay + 0.05);

  // Brighter strike transient on top of the wash so the attack reads as
  // a hit, not just a swell. Its own high-pass (brighter than the wash)
  // plus a matching low-pass ceiling keeps it band-limited so it doesn't
  // re-introduce the hiss the wash's low-pass removed.
  const strike = ctx.createBufferSource();
  strike.buffer = makeNoiseBuffer(ctx, 0.03);
  const strikeHp = ctx.createBiquadFilter();
  strikeHp.type = 'highpass';
  strikeHp.frequency.value = Math.min(p.hpHz * 1.6, p.lpHz * 0.9);
  const strikeLp = ctx.createBiquadFilter();
  strikeLp.type = 'lowpass';
  strikeLp.frequency.value = p.lpHz;
  strikeLp.Q.value = 0.5;
  const strikeGain = ctx.createGain();
  strikeGain.gain.setValueAtTime(p.gain * 0.9 * CIRCLES_HS_TRIM, t0);
  strikeGain.gain.exponentialRampToValueAtTime(0.0008, t0 + 0.04);
  strike.connect(strikeHp).connect(strikeLp).connect(strikeGain).connect(destination);
  strike.start(t0);
  return p.decay + 0.05;
}

/**
 * Clap: the backbeat snare/clap. A 3-burst "flam" attack (three quick
 * band-passed noise hits a few ms apart) gives the characteristic
 * hand-clap slap, followed by a short decaying tail. Band-pass centre
 * sets the brightness per bank.
 */
function scheduleClap(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
  p: BankParams['clap'],
): number {
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = p.bpHz;
  bp.Q.value = 1.3;
  bp.connect(destination);

  const attackSec = p.attackMs / 1000;
  // Three closely-spaced bursts smear the attack into a clap "slap".
  const burstOffsets = [0, 0.009, 0.018];
  for (const offset of burstOffsets) {
    const start = t0 + offset + attackSec * (offset === 0 ? 0 : 1);
    const burst = ctx.createBufferSource();
    burst.buffer = makeNoiseBuffer(ctx, 0.012);
    const burstGain = ctx.createGain();
    burstGain.gain.setValueAtTime(p.gain * 0.7 * CIRCLES_HS_TRIM, start);
    burstGain.gain.exponentialRampToValueAtTime(0.0008, start + 0.014);
    burst.connect(burstGain).connect(bp);
    burst.start(start);
  }
  // Decaying tail after the flam so the clap has body, not just a tick.
  const tail = ctx.createBufferSource();
  tail.buffer = makeNoiseBuffer(ctx, p.decay);
  const tailGain = ctx.createGain();
  const tailStart = t0 + 0.02;
  tailGain.gain.setValueAtTime(p.gain * CIRCLES_HS_TRIM, tailStart);
  tailGain.gain.exponentialRampToValueAtTime(0.0006, tailStart + p.decay);
  tail.connect(tailGain).connect(bp);
  tail.start(tailStart);
  return 0.02 + p.decay;
}
